import React from "react";

import * as Icons from "./EventIcons";

type BaseEvent = {
  displayText: string;
  value: string;
  //extend metadata object to contain calendar types
  metadata: {
    colors?: string[];
    associatedReligions?: Array<string>;
    icon?: JSX.Element;
  };
};
type CalendarEvent = BaseEvent & {
  startDate: string;
  endDate: string;
  TypeOfEvent: "Calendar";
  /** Source for the date - helps track where floating dates came from */
  dateSource?: "fixed" | "hebcal.com" | "timeanddate.com" | "calendardate.com";
};

type PersistentEvent = BaseEvent & { TypeOfEvent: "Persistent" };

export type EventType = CalendarEvent | PersistentEvent;

export const eventsArray: Array<EventType> = [
  // 2026 Holiday Dates
  // Sources: hebcal.com, calendardate.com, timeanddate.com
  // TODO: Replace with dynamic holiday API (see LLD)
  {
    displayText: "MLK Day",
    startDate: "01-19-2026", // 3rd Monday of January
    endDate: "01-19-2026",
    value: "MLK Day",
    TypeOfEvent: "Calendar",
    dateSource: "timeanddate.com",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.PeaceDove />,
      colors: ["#1a5276"],
    },
  },
  {
    displayText: "Valentine's Day",
    startDate: "02-14-2026",
    endDate: "02-14-2026",
    value: "Valentines Day",
    TypeOfEvent: "Calendar",
    dateSource: "fixed",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.ValentinesChocolateHeart />,
      colors: ["#f01a1a"],
    },
  },
  {
    displayText: "Presidents Day",
    startDate: "02-16-2026", // 3rd Monday of February
    endDate: "02-16-2026",
    value: "Presidents Day",
    TypeOfEvent: "Calendar",
    dateSource: "timeanddate.com",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.PresidentStar />,
      colors: ["#1e3a5f"],
    },
  },
  {
    displayText: "Lunar New Year",
    startDate: "02-17-2026", // Year of the Horse
    endDate: "03-03-2026", // Ends with Lantern Festival
    value: "Lunar New Year",
    TypeOfEvent: "Calendar",
    dateSource: "timeanddate.com",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.LunarNewYearLamp />,
    },
  },
  {
    displayText: "St. Patrick's Day",
    startDate: "03-17-2026",
    endDate: "03-17-2026",
    value: "St Patricks Day",
    TypeOfEvent: "Calendar",
    dateSource: "fixed",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.Shamrock />,
      colors: ["#009A44"],
    },
  },
  {
    displayText: "April Fools' Day",
    startDate: "04-01-2026",
    endDate: "04-01-2026",
    value: "April Fools Day",
    TypeOfEvent: "Calendar",
    dateSource: "fixed",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.JesterHat />,
    },
  },
  {
    displayText: "Earth Day",
    startDate: "04-22-2026",
    endDate: "04-22-2026",
    value: "Earth Day",
    TypeOfEvent: "Calendar",
    dateSource: "fixed",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.EarthDay />,
    },
  },
  {
    displayText: "Mother's Day",
    startDate: "05-10-2026", // 2nd Sunday of May
    endDate: "05-10-2026",
    value: "Mothers Day",
    TypeOfEvent: "Calendar",
    dateSource: "timeanddate.com",
    metadata: {
      colors: ["#FE3A3A"],
      associatedReligions: ["NA", "none"],
      icon: <Icons.Rose />,
    },
  },
  {
    displayText: "Memorial Day",
    startDate: "05-25-2026", // Last Monday of May
    endDate: "05-25-2026",
    value: "Memorial Day",
    TypeOfEvent: "Calendar",
    dateSource: "timeanddate.com",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.Flag />,
    },
  },
  {
    displayText: "Father's Day",
    startDate: "06-21-2026", // 3rd Sunday of June
    endDate: "06-21-2026",
    value: "Fathers Day",
    TypeOfEvent: "Calendar",
    dateSource: "timeanddate.com",
    metadata: {
      colors: ["#EC892B"],
      associatedReligions: ["NA", "none"],
      icon: <Icons.Tie />,
    },
  },
  {
    displayText: "Independence Day",
    startDate: "07-04-2026",
    endDate: "07-04-2026",
    value: "Independence Day",
    TypeOfEvent: "Calendar",
    dateSource: "fixed",
    metadata: {
      colors: ["#FF0000", "#FFFFFF", "#0000FF"],
      associatedReligions: ["NA", "none"],
      icon: <Icons.Fireworks />,
    },
  },
  {
    displayText: "Labor Day",
    startDate: "09-07-2026", // 1st Monday of September
    endDate: "09-07-2026",
    value: "Labor Day",
    TypeOfEvent: "Calendar",
    dateSource: "timeanddate.com",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.HardHat />,
    },
  },
  {
    displayText: "Rosh Hashana",
    startDate: "09-11-2026", // Begins at sundown; 1-2 Tishrei 5787
    endDate: "09-13-2026",
    value: "Rosh Hashana",
    TypeOfEvent: "Calendar",
    dateSource: "hebcal.com",
    metadata: {
      associatedReligions: ["jewish"],
      icon: <Icons.Shofar />,
    },
  },
  {
    displayText: "Halloween",
    startDate: "10-31-2026",
    endDate: "10-31-2026",
    value: "Halloween",
    TypeOfEvent: "Calendar",
    dateSource: "fixed",
    metadata: {
      colors: ["#e68f39"],
      associatedReligions: ["NA", "none"],
      icon: <Icons.JackoLantern />,
    },
  },
  {
    displayText: "Thanksgiving",
    startDate: "11-26-2026", // 4th Thursday of November
    endDate: "11-26-2026",
    value: "Thanksgiving",
    TypeOfEvent: "Calendar",
    dateSource: "calendardate.com",
    metadata: {
      colors: ["#c26102"],
      associatedReligions: ["NA", "none"],
      icon: <Icons.Turkey />,
    },
  },
  {
    displayText: "Chanukah",
    startDate: "12-04-2026", // Begins at sundown; 25 Kislev 5787
    endDate: "12-12-2026",
    value: "Chanukah",
    TypeOfEvent: "Calendar",
    dateSource: "hebcal.com",
    metadata: {
      associatedReligions: ["jewish"],
      icon: <Icons.Menorah />,
    },
  },
  {
    displayText: "Christmas",
    startDate: "12-25-2026",
    endDate: "12-31-2026",
    value: "Christmas",
    TypeOfEvent: "Calendar",
    dateSource: "fixed",
    metadata: {
      colors: ["#FF0000", "white", "green"],
      associatedReligions: ["christian", "none"],
      icon: <Icons.ChristmasTree />,
    },
  },
  {
    displayText: "New Year's",
    startDate: "01-01-2027",
    endDate: "01-02-2027",
    value: "New Years",
    TypeOfEvent: "Calendar",
    dateSource: "fixed",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.ChanpagneGlasses />,
    },
  },
  {
    displayText: "Birthday",
    value: "Birthday",
    TypeOfEvent: "Persistent",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.BirthdayCupcake />,
      colors: ["#4287f5"],
    },
  },
  {
    displayText: "Wedding",
    value: "Wedding",
    TypeOfEvent: "Persistent",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.WeddingCake />,
    },
  },
  {
    displayText: "Graduation",
    value: "Graduation",
    metadata: {
      colors: ["#0f9ad6"],
      associatedReligions: ["NA", "none"],
      icon: <Icons.GraduationCap />,
    },
    TypeOfEvent: "Persistent",
  },
  {
    displayText: "Get Well Soon",
    value: "Get Well Soon",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.TeaLeaves />,
    },
    TypeOfEvent: "Persistent",
  },
  {
    displayText: "Thank You",
    value: "Thank You",
    TypeOfEvent: "Persistent",
    metadata: {
      associatedReligions: ["NA", "none"],
      icon: <Icons.ThankYouHeart />,
      colors: ["#e84393"],
    },
  },
  {
    displayText: "Teacher's Appreciation",
    value: "Teachers appreciation",
    metadata: {
      colors: ["#5e1db8"],
      associatedReligions: ["NA", "none"],
      icon: <Icons.Books />,
    },
    TypeOfEvent: "Persistent",
  },
];
